import { normalize } from 'normalizr';
import { call, put, select, takeEvery } from 'redux-saga/effects';

import partService, { type CadTree } from 'services/partService';

import type { ActionOfType } from 'store/types';

import {
  types,
  fetchPartFileCadStructure,
  type PartFileCadDataAction,
} from './actions';
import { viewerBodySchema } from './entities';
import { getPartFileCadDataExists } from './reducer';
import type { PartFileCadEntities } from './types';

export default function* root() {
  yield takeEvery(
    types.PART_FILE_BULK_CAD_DATA_RECEIVE,
    watchReceiveBulkPartFileCadData,
  );
}

// Bulk handler for when # of files is over a limit to prevent 429s
function* watchReceiveBulkPartFileCadData(
  action: ActionOfType<
    PartFileCadDataAction,
    typeof types.PART_FILE_BULK_CAD_DATA_RECEIVE
  >,
) {
  const partFileUuids = action?.payload?.partFileUuids ?? [];
  try {
    const noCadDataPartFileUuids: string[] = [];
    for (const partFileUuid of partFileUuids) {
      const partFileCadDataExists: ReturnType<typeof getPartFileCadDataExists> =
        yield select(getPartFileCadDataExists, partFileUuid);
      if (!partFileCadDataExists) {
        noCadDataPartFileUuids.push(partFileUuid);
        yield put(
          fetchPartFileCadStructure.init({
            partFileUuid,
          }),
        );
      }
    }

    if (noCadDataPartFileUuids.length) {
      // only fetch data if we have not already loaded it for this file
      let partFileCadTrees: CadTree[];
      try {
        partFileCadTrees = yield call(
          partService.partFiles.bulkCadTree.get,
          noCadDataPartFileUuids,
        );
      } catch (error) {
        console.error('Failed to fetch part file cad tree', error);
        for (const partFileUuid of partFileUuids) {
          yield put(fetchPartFileCadStructure.error({ partFileUuid }));
        }
        return;
      }

      for (const {
        viewerBody: rootViewerBody,
        uuid,
      } of partFileCadTrees.values()) {
        // NB: normalizr can't infer output types; these generics assert the
        // shape produced by viewerBodySchema (see ./entities)
        const { entities, result: rootViewerBodyViewerId } = normalize<
          unknown,
          PartFileCadEntities,
          string
        >(rootViewerBody, viewerBodySchema);
        yield put(
          fetchPartFileCadStructure.success({
            entities,
            partFileUuid: uuid,
            rootViewerBodyViewerId,
          }),
        );
      }
    }
  } catch (error) {
    console.error(error);
  }
}
